/**
 * ChatDock — the Clubhouse: one global, ephemeral chat room, floating on every
 * portal page. Mostly "hi" and emoji between the people the presence rail
 * already shows are around.
 *
 * Mounted ONCE at the App root (the usePresenceHeartbeat precedent) so it
 * survives route changes, and it renders NOTHING at all unless the viewer is
 * signed in with a claimed handle — the room is signed-in-only (the rail above
 * it is public; the conversation is not), and a dock that only tells you to log
 * in is clutter on a page you came to for games.
 *
 * Ephemerality is retention, not transport: messages live 24h, enforced by RLS
 * and swept by the post callable. The dock just renders whatever the window
 * currently holds.
 *
 * Fixed bottom-right, minimizing to a bubble with an unread count. Deliberately
 * NOT draggable or resizable — that would buy a dependency and a pile of
 * pointer-event edge cases for a panel nobody needs to move.
 *
 * Text and emoji only. A GIF picker was speced beside the emoji one, but Google
 * decommissioned the Tenor API on 2026-06-30 and no replacement was adopted, so
 * it was removed rather than left half-wired. Emoji are just characters in the
 * body — the picker inserts, the server never sees a special case.
 */
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CHAT_MAX_LEN, type ChatMessage } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { Mascot } from '../components/Mascot.js';
import { UserAvatar } from '../components/UserAvatar.js';
import { useChatDock } from './chatDockContext.js';
import { useCanChat } from './useCanChat.js';
import { newestAt, readWatermark, unreadCount, writeWatermark } from './unread.js';

/** The emoji picker and its dataset are big and most sessions never open them —
 *  keep them in their own chunk (see EmojiPad's header). */
const EmojiPad = lazy(() => import('./EmojiPad.js'));

/** Coarse "how long ago" label (client clock — this is UI, not the sim).
 *  Capped at hours: nothing in this room is older than a day. */
function timeAgo(ts: number): string {
  if (!ts) return '';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
}

/** One line in the room. */
function ChatRow({
  message,
  canDelete,
  onDelete,
}: {
  message: ChatMessage & { id: string };
  canDelete: boolean;
  onDelete: (id: string) => void;
}) {
  return (
    <li className="chat__row" data-testid="chat-row">
      <UserAvatar avatarKey={message.avatarKey} size={24} alt={`@${message.handle}`} />
      <div className="chat__row-body">
        <div className="chat__row-meta">
          <Link className="chat__row-handle" to={`/u/${message.handle}`}>
            @{message.handle}
          </Link>
          <span className="chat__row-time muted">{timeAgo(message.createdAt)}</span>
          {canDelete && (
            <button
              className="btn btn--ghost btn--sm chat__row-delete"
              onClick={() => onDelete(message.id)}
              title="Delete message"
              aria-label="Delete message"
              data-testid={`chat-delete-${message.id}`}
            >
              ✕
            </button>
          )}
        </div>
        <p className="chat__text">{message.body}</p>
      </div>
    </li>
  );
}

export function ChatDock() {
  const { user } = useAuth();
  const services = useServices();
  const dock = useChatDock();

  const uid = user?.uid ?? '';
  // The room's entry fee (see useCanChat) — anyone else gets no dock at all.
  const canChat = useCanChat();
  const isAdmin = user?.role === 'superadmin';

  // Newest-first off the wire (the natural "last 50" slice); reversed for
  // display so the newest line sits at the bottom, where a chat's eye goes.
  const rows = useSubscription<Array<ChatMessage & { id: string }>>(
    (cb, onError) => {
      if (!canChat) {
        cb([]);
        return () => {};
      }
      return services.subscribe.chat(cb, onError);
    },
    [],
    [services, canChat],
  );
  const messages = useMemo(() => [...rows].reverse(), [rows]);

  const [draft, setDraft] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [watermark, setWatermark] = useState(() => (canChat ? readWatermark() : 0));
  const listRef = useRef<HTMLOListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const open = dock?.open ?? false;
  const seed = dock?.seed ?? null;
  const unread = unreadCount(messages, watermark, uid);

  // Someone handed the dock a draft (the presence rail's "say hi"). Keyed on the
  // nonce so clicking the same person twice seeds twice.
  useEffect(() => {
    if (!seed) return;
    setDraft(seed.text);
    inputRef.current?.focus();
  }, [seed]);

  // Reading happens by LOOKING: while the dock is open, every message that
  // arrives is read. Closed, the watermark stands still and the badge counts up.
  useEffect(() => {
    if (!open || !canChat) return;
    const newest = newestAt(messages);
    if (newest <= 0) return;
    writeWatermark(newest);
    setWatermark((prev) => (newest > prev ? newest : prev));
  }, [open, canChat, messages]);

  // Pin to the newest line whenever the room grows or the dock opens.
  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [open, messages.length]);

  const send = useCallback(
    async (payload: Parameters<typeof services.callable.postChatMessage>[0]) => {
      if (busy) return;
      setBusy(true);
      setError('');
      try {
        await services.callable.postChatMessage(payload);
        setDraft('');
        setEmojiOpen(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not send that.');
      } finally {
        setBusy(false);
      }
    },
    [busy, services],
  );

  const sendText = useCallback(() => {
    const body = draft.trim();
    if (!body) return;
    void send({ body });
  }, [draft, send]);

  const remove = useCallback(
    async (messageId: string) => {
      setError('');
      try {
        await services.callable.deleteChatMessage({ messageId });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not delete that.');
      }
    },
    [services],
  );

  if (!canChat || !dock) return null;

  if (!open) {
    return (
      <button
        className="chat-bubble"
        onClick={() => dock.openChat()}
        aria-label={unread > 0 ? `Open chat, ${unread} unread` : 'Open chat'}
        data-testid="chat-bubble"
      >
        <span aria-hidden="true">💬</span>
        {unread > 0 && (
          <span className="chat-bubble__badge" data-testid="chat-unread">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <section className="chat" data-testid="chat-dock" aria-label="Clubhouse chat">
      <header className="chat__head">
        <span className="chat__title">The Clubhouse</span>
        <span className="chat__subtitle muted">disappears after a day</span>
        <button
          className="btn btn--ghost btn--sm chat__minimize"
          onClick={dock.minimizeChat}
          aria-label="Minimize chat"
          data-testid="chat-minimize"
        >
          –
        </button>
      </header>

      {messages.length === 0 ? (
        <div className="chat__empty" data-testid="chat-empty">
          <Mascot context="chat-empty" size={180} quip={false} />
          <p className="muted">Quiet in here. Say hi.</p>
        </div>
      ) : (
        <ol className="chat__list" ref={listRef}>
          {messages.map((message) => (
            <ChatRow
              key={message.id}
              message={message}
              canDelete={message.uid === uid || isAdmin}
              onDelete={(id) => void remove(id)}
            />
          ))}
        </ol>
      )}

      {error && (
        <p className="error-text chat__error" data-testid="chat-error">
          {error}
        </p>
      )}

      {emojiOpen && (
        <Suspense fallback={<p className="muted chat__picker-note">Loading emoji…</p>}>
          <EmojiPad onPick={(emoji) => setDraft((d) => d + emoji)} />
        </Suspense>
      )}

      <div className="chat__composer">
        <button
          className={`btn btn--ghost btn--sm ${emojiOpen ? 'is-active' : ''}`}
          onClick={() => setEmojiOpen((open) => !open)}
          aria-label="Emoji"
          data-testid="chat-emoji-toggle"
        >
          🙂
        </button>
        <input
          ref={inputRef}
          className="input chat__input"
          placeholder="Say something…"
          value={draft}
          maxLength={CHAT_MAX_LEN}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendText();
            }
          }}
          data-testid="chat-input"
        />
        <button
          className="btn btn--primary btn--sm"
          onClick={sendText}
          disabled={busy || draft.trim().length === 0}
          data-testid="chat-send"
        >
          Send
        </button>
      </div>
    </section>
  );
}
